import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_PREFIX = 'pcc:restore:';

// Products stream in after the route mounts, so the page is short at first and
// the browser cannot scroll to the saved offset yet. Keep re-trying each frame
// until the document is tall enough, then give up rather than fight the user.
const RESTORE_TIMEOUT_MS = 3000;

interface StoredEntry<T> {
    scrollY: number;
    state: T | null;
}

function readEntry<T>(key: string): StoredEntry<T> | null {
    try {
        const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
        return raw ? (JSON.parse(raw) as StoredEntry<T>) : null;
    } catch {
        return null;
    }
}

function writeEntry<T>(key: string, entry: StoredEntry<T>): void {
    try {
        sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    } catch {
        // Private mode or a full quota: restoration is a nicety, never an error.
    }
}

interface ScrollRestorationOptions<T> {
    // Anything the page needs back before the saved offset makes sense again -
    // the page number a product was clicked from, the active filters, the sort.
    state?: T;
    onRestore?: (state: T) => void;
    // False while the list is still loading, so the retry window does not burn
    // its timeout waiting on the API.
    ready?: boolean;
}

/**
 * Restores scroll position (and optional list state) when the user comes back
 * to this page through the browser's back button. Every history entry keeps its
 * own snapshot, so returning to a category lands on the product that was
 * clicked, not on the top of page one.
 */
export function useScrollRestoration<T>({
    state,
    onRestore,
    ready = true,
}: ScrollRestorationOptions<T> = {}): void {
    const { key } = useLocation();
    const navigationType = useNavigationType();

    const stateRef = useRef(state);
    stateRef.current = state;
    const onRestoreRef = useRef(onRestore);
    onRestoreRef.current = onRestore;

    // Non-null while a restore is pending, which also blocks saving: the page is
    // sitting at the top at that moment and would otherwise overwrite the offset
    // we are about to restore.
    const pendingScrollRef = useRef<number | null>(null);

    // The offset to persist, kept up to date from scroll events instead of read
    // back from the window on the way out. By the time this page is torn down
    // the next route has already reset the window to the top, so window.scrollY
    // no longer describes where the shopper was.
    const trackedScrollRef = useRef(0);

    // Read during the first render rather than from a mount effect: the layout
    // effect below persists on mount, and layout effects run first, so an effect
    // would only ever find the entry it had just overwritten.
    const snapshotRef = useRef<StoredEntry<T> | null | undefined>(undefined);
    if (snapshotRef.current === undefined) {
        // A fresh visit or a new link click starts wherever it starts; only
        // back/forward navigation should reopen a snapshot.
        const saved = navigationType === 'POP' ? readEntry<T>(key) : null;
        snapshotRef.current = saved;
        pendingScrollRef.current = saved ? saved.scrollY : null;
        trackedScrollRef.current = saved ? saved.scrollY : window.scrollY;
    }

    useEffect(() => {
        const saved = snapshotRef.current;
        if (!saved || saved.state === null || !onRestoreRef.current) return;
        onRestoreRef.current(saved.state);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!ready || pendingScrollRef.current === null) return;

        const target = pendingScrollRef.current;
        const startedAt = performance.now();
        let frame = 0;

        const finish = () => {
            pendingScrollRef.current = null;
            cancelAnimationFrame(frame);
            window.removeEventListener('wheel', finish);
            window.removeEventListener('touchstart', finish);
            window.removeEventListener('keydown', finish);
        };

        const step = () => {
            const maxScroll = Math.max(
                document.documentElement.scrollHeight - window.innerHeight,
                0
            );
            window.scrollTo({ top: Math.min(target, maxScroll), behavior: 'auto' });

            if (maxScroll >= target || performance.now() - startedAt > RESTORE_TIMEOUT_MS) {
                finish();
                return;
            }
            frame = requestAnimationFrame(step);
        };

        // The user reaching for the wheel wins over anything we still owe them.
        window.addEventListener('wheel', finish, { passive: true });
        window.addEventListener('touchstart', finish, { passive: true });
        window.addEventListener('keydown', finish);
        frame = requestAnimationFrame(step);

        return finish;
    }, [ready]);

    const serializedState = JSON.stringify(state ?? null);

    // Deliberately a layout effect: React tears this down in the mutation phase,
    // before the incoming route's own layout effect scrolls back to the top, so
    // the listener is gone before that reset can be mistaken for the shopper
    // scrolling up.
    useLayoutEffect(() => {
        const save = () => {
            if (pendingScrollRef.current !== null) return;
            writeEntry(key, {
                scrollY: trackedScrollRef.current,
                state: stateRef.current ?? null,
            });
        };

        // Safe to read the window here and only here: this page is on screen,
        // so the offset is its own. A pending restore is the exception - the
        // tracked value is the target we are still working towards.
        const track = () => {
            if (pendingScrollRef.current === null) {
                trackedScrollRef.current = window.scrollY;
            }
        };

        let queued = false;
        const onScroll = () => {
            track();
            if (queued) return;
            queued = true;
            requestAnimationFrame(() => {
                queued = false;
                save();
            });
        };

        track();
        save();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            save();
        };
    }, [key, serializedState]);
}

export default useScrollRestoration;
